import { domMeasurements } from '#packages/stateManagement';
import { createSnapHandler } from './resizeGapsSnapHandler';
import { createTransition } from './resizeGapsTransition';

import type { MouseEvent } from 'react';
import type { EditorAPI } from '#packages/editorAPI';
import type { Gap } from './resizeGapsTypes';

const { translateToViewerCoordinates } = domMeasurements.selectors;

interface Params {
  gap: Gap;
  afterStart: () => void;
  afterOn: (deltaY: number) => void;
  afterEnd: () => void;
}

export const createMouseHandler = (
  editorAPI: EditorAPI,
  e: MouseEvent,
  params: Params,
) => {
  const { gap, afterStart, afterOn, afterEnd } = params;
  const snapChecker = createSnapHandler(editorAPI, gap);

  const initMouseCoords = translateToViewerCoordinates(editorAPI, e);

  const transition = createTransition(editorAPI, gap);

  return {
    start: () => {
      afterStart();
    },
    on: (event: MouseEvent) => {
      const mouseCoords = translateToViewerCoordinates(editorAPI, event);
      const minDeltaYPossible = -gap.layout.height;
      const deltaY = Math.max(
        minDeltaYPossible,
        (mouseCoords.pageY - initMouseCoords.pageY) * 2,
      );

      const { snapData, deltaY: newDeltaY } =
        snapChecker.getSnapDataAndModifiedDeltaY(deltaY);
      editorAPI.snapData.set(snapData);

      transition.update({ deltaY: newDeltaY });

      afterOn(newDeltaY);
    },
    end: () => {
      editorAPI.snapData.clear();
      transition.end();
      afterEnd();
    },
    type: transition.type,
  };
};
